"use client";

import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";

/**
 * Compact mic button. Click to start recording (browser MediaRecorder API,
 * audio/webm), click again to stop. On stop:
 *   1. Upload the blob to the workspace's `attachments` bucket.
 *   2. Optionally POST the URL to /api/transcribe — if OPENAI_API_KEY is
 *      set server-side, returns Whisper text. (Failing transcription is
 *      not an error; voice note still inserts.)
 *   3. Call onInserted with a markdown snippet ready to drop at the
 *      cursor: `[🎙️ voice-note (Ns).webm](url)` + transcript blockquote.
 *
 * Designed for MarkdownEditor + CommentComposer. PAUT-68.
 */
export function VoiceRecorder({
  onInserted,
  className,
}: {
  onInserted: (snippet: string) => void;
  className?: string;
}) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [state, setState] = useState<"idle" | "recording" | "uploading" | "transcribing">(
    "idle"
  );
  const [seconds, setSeconds] = useState(0);

  // Tick the timer while recording.
  useEffect(() => {
    if (state !== "recording") return;
    setSeconds(0);
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  async function start() {
    if (state !== "idle") return;
    if (!activeWorkspaceId) {
      console.warn("[voice] no active workspace");
      return;
    }
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      alert("Your browser doesn't support audio recording.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        await handleBlob(new Blob(chunksRef.current, { type: rec.mimeType }), seconds);
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
    } catch (err) {
      console.error("[voice] getUserMedia failed", err);
      alert("Microphone permission was denied.");
    }
  }

  function stop() {
    const rec = recRef.current;
    if (!rec) return;
    if (rec.state !== "inactive") rec.stop();
    recRef.current = null;
  }

  async function handleBlob(blob: Blob, durationSec: number) {
    if (!activeWorkspaceId) return;
    setState("uploading");
    try {
      const sb = createClient();
      const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
      const path = `${activeWorkspaceId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await sb.storage.from("attachments").upload(path, blob, {
        cacheControl: "3600",
        upsert: false,
        contentType: blob.type || "audio/webm",
      });
      if (error) {
        console.error("[voice] upload failed", error);
        return;
      }
      const { data: pub } = sb.storage.from("attachments").getPublicUrl(path);
      const url = pub.publicUrl;
      const sizeStr = formatBytes(blob.size);
      const baseSnippet = `\n[🎙️ voice-note (${durationSec}s, ${sizeStr}).${ext}](${url})\n`;

      // Best-effort transcription. Server returns 503 when not configured;
      // we silently skip in that case.
      setState("transcribing");
      let transcript: string | null = null;
      try {
        const r = await fetch("/api/transcribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        });
        if (r.ok) {
          const { text } = (await r.json()) as { text?: string };
          if (text?.trim()) transcript = text.trim();
        }
      } catch {
        // Network/transcription failure → just skip, we still have the audio.
      }

      const snippet = transcript
        ? baseSnippet +
          `\n> 📝 ${transcript.replace(/\n+/g, " ")}\n`
        : baseSnippet;
      onInserted(snippet);
    } finally {
      setState("idle");
      setSeconds(0);
    }
  }

  const isBusy = state === "uploading" || state === "transcribing";

  return (
    <button
      onClick={state === "recording" ? stop : start}
      disabled={isBusy}
      className={cn(
        "inline-flex items-center gap-1 px-2 h-7 rounded-md border text-[11.5px] transition-colors",
        state === "recording"
          ? "bg-[var(--red)]/15 border-[var(--red)]/40 text-[var(--red)] animate-pulse"
          : "bg-[var(--bg-tertiary)] border-[var(--border)] text-[var(--fg-tertiary)] hover:text-[var(--fg)] hover:border-[var(--border-strong)]",
        isBusy && "opacity-60 cursor-not-allowed",
        className
      )}
      title={
        state === "recording"
          ? "Stop recording"
          : state === "uploading"
          ? "Uploading…"
          : state === "transcribing"
          ? "Transcribing…"
          : "Record voice note"
      }
    >
      {state === "recording" ? (
        <>
          <Square size={11} fill="currentColor" />
          <span className="font-mono">{formatTime(seconds)}</span>
        </>
      ) : isBusy ? (
        <>
          <Loader2 size={11} className="animate-spin" />
          {state === "transcribing" ? "Transcribing…" : "Uploading…"}
        </>
      ) : (
        <>
          <Mic size={11} />
          Voice note
        </>
      )}
    </button>
  );
}

function pickMime(): string | null {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return null;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
